import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from 'react';
import { eventService } from '../services/api';
import { Event } from '../types';
import { useAuth } from './AuthContext';
import { eventBus, EVENTS } from '../utils/eventBus';

interface EventContextType {
  events: Event[];
  currentEvent: Event | null;
  loading: boolean;
  error: string | null;
  setCurrentEvent: (event: Event | null) => void;
  refreshEvents: () => Promise<void>;
}

const EventContext = createContext<EventContextType | null>(null);

export const useEvent = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvent must be used within EventProvider');
  }
  return context;
};

interface EventProviderProps {
  children: ReactNode;
}

export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleSetCurrentEvent = (event: Event | null) => {
    setCurrentEvent(event);
    if (event) {
      localStorage.setItem('currentEventId', event._id);
    } else {
      localStorage.removeItem('currentEventId');
    }
  };

  const refreshEvents = async () => {
    try {
      // Temporarily use getAllEvents to debug
      const response = await eventService.getAllEvents();
      console.log('Refresh - all events:', response);
      const eventsArray = Array.isArray(response) ? response : [];
      setEvents(eventsArray);

      // If current event is no longer in the list, select the first one
      if (
        currentEvent &&
        !eventsArray.find((e) => e._id === currentEvent._id)
      ) {
        if (eventsArray.length > 0) {
          handleSetCurrentEvent(eventsArray[0]);
        } else {
          handleSetCurrentEvent(null);
        }
      } else if (!currentEvent && eventsArray.length > 0) {
        handleSetCurrentEvent(eventsArray[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to refresh events');
      console.error('Failed to refresh events', err);
    }
  };

  useEffect(() => {
    const loadEvents = async () => {
      if (!user) {
        setLoading(false);
        setEvents([]);
        setCurrentEvent(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Use getAllEvents for now to ensure we see events
        const response = await eventService.getAllEvents();
        console.log('All events response:', response);

        const eventsArray = Array.isArray(response) ? response : [];

        // Don't filter events here - let the UI handle showing/hiding based on status
        // This ensures all events are available for selection
        setEvents(eventsArray);

        // Load saved event from localStorage
        const savedEventId = localStorage.getItem('currentEventId');
        if (savedEventId && eventsArray.length > 0) {
          const savedEvent = eventsArray.find((e) => e._id === savedEventId);
          if (savedEvent) {
            handleSetCurrentEvent(savedEvent);
          } else {
            // If saved event not found, select the first active event
            const activeEvents = eventsArray.filter(
              (e) => e.status === 'active'
            );
            if (activeEvents.length > 0) {
              handleSetCurrentEvent(activeEvents[0]);
            }
          }
        } else if (!savedEventId && eventsArray.length > 0) {
          // No saved event, select the first active event
          const activeEvents = eventsArray.filter((e) => e.status === 'active');
          if (activeEvents.length > 0) {
            handleSetCurrentEvent(activeEvents[0]);
          }
        }
      } catch (err: any) {
        console.error('Failed to load events:', err);
        setError(
          err.response?.data?.message || err.message || 'Failed to load events'
        );
      }

      setLoading(false);
    };

    loadEvents();

    // Subscribe to event bus for refreshing events
    const unsubscribeEvents = eventBus.on(EVENTS.REFRESH_EVENTS, () => {
      console.log('EventContext: Refreshing events due to import');
      refreshEvents();
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribeEvents();
    };
  }, [user]);

  const value = {
    events,
    currentEvent,
    loading,
    error,
    setCurrentEvent: handleSetCurrentEvent,
    refreshEvents,
  };

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
};
